import { Routes, Route, Navigate, useLocation, useParams } from 'react-router';
import { Layout } from './components/Layout';
import { IdentityVerificationShell } from './components/IdentityVerificationShell';
import { ApplicationsPage } from './pages/ApplicationsPage';
import { ApplicationDetailPage } from './pages/ApplicationDetailPage';
import { DoctrineConfigPage } from './pages/DoctrineConfigPage';
import { DoctrineDetailPage } from './pages/DoctrineDetail';
import { ProvidersPage } from './pages/Providers';
import { ProviderDetailPage } from './pages/ProviderDetail';
import { RoutingPage } from './pages/Routing';
import { LoginPage } from './pages/Login';
import { MfaPage } from './pages/MfaPage';
import { EmployerVerificationDashboard } from './pages/EmployerVerificationDashboard';
import { EmployerVerificationAnalyticsDashboard } from './pages/EmployerVerificationAnalyticsDashboard';
import { CompanyDossierPage } from './pages/CompanyDossierPage';
import { EmployerInvestigationWorkspacePage } from './pages/EmployerInvestigationWorkspacePage';
import { IncomeVerificationDashboard } from './pages/IncomeVerificationDashboard';
import { IdentityVerificationDashboard } from './pages/IdentityVerificationDashboard';
import { IdentityVerificationJourneys } from './pages/IdentityVerificationJourneys';
import { IdentityVerificationLookup } from './pages/IdentityVerificationLookup';
import { AdminUsersPage } from './pages/AdminUsers';
import { AdminGroupsPage } from './pages/AdminGroups';
import { AdminRolesPage } from './pages/AdminRoles';
import { AdminUserDetailPage } from './pages/AdminUserDetail';
import { AdminExpiringAccessPage } from './pages/AdminExpiringAccess';
import { AdminConsoleAuditPage } from './pages/AdminConsoleAudit';
import { AdminAccessReviewsPage } from './pages/AdminAccessReviews';
import { AdminAccessReviewDetailPage } from './pages/AdminAccessReviewDetail';
import { AdminEligibilitiesPage } from './pages/AdminEligibilities';
import { MyAccessPage } from './pages/MyAccess';
import { NoAccessPage } from './pages/NoAccess';
import { Vendors } from './pages/Vendors';
import { ServiceOnboardingPage } from './pages/ServiceOnboardingPage';
import { ServiceOnboardingEditorPage } from './pages/ServiceOnboardingEditorPage';
import { rememberedApplicationsListPath } from './lib/applicationsListFilters';
import { stripLegacyAdminPrefix } from './config/paths';
import { OidcSessionGate } from './components/OidcSessionGate';
import { AuthCallbackPage } from './pages/AuthCallback';
import { adminOidcConfig, isOidcAdminAuth } from './lib/oidc/oidcConfig';

/** Bookmarks like `/admin/applications/:id` after the SPA moved to the host root. */
function RedirectLegacyAdminPrefix() {
  const location = useLocation();
  const nextPath = stripLegacyAdminPrefix(location.pathname);
  return (
    <Navigate
      to={{ pathname: nextPath, search: location.search, hash: location.hash }}
      replace
    />
  );
}

function RedirectLegacyDoctrineDetail() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/doctrine-config" replace />;
  return <Navigate to={`/doctrine-config/${encodeURIComponent(id)}`} replace />;
}

/** Soft redirects to the applications list keep the last filter query when present. */
function RedirectToApplications() {
  return <Navigate to={rememberedApplicationsListPath()} replace />;
}

/** OIDC redirect route (`VITE_ADMIN_OIDC_REDIRECT_PATH`, default `/auth/callback`), router-relative. */
function oidcCallbackRoutePath(): string | null {
  if (!isOidcAdminAuth()) return null;
  try {
    return adminOidcConfig().redirectPath.replace(/^\/+/, '');
  } catch {
    return 'auth/callback'; // VITE_AUTH_SERVER_BASE missing — the callback page reports it
  }
}

export function App() {
  const callbackPath = oidcCallbackRoutePath();
  return (
    <Routes>
      {callbackPath && <Route path={callbackPath} element={<AuthCallbackPage />} />}
      <Route path="admin/*" element={<RedirectLegacyAdminPrefix />} />
      <Route path="income-verification-dashboard" element={<Navigate to="/income-verification" replace />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="mfa/challenge" element={<MfaPage />} />
      <Route path="mfa/recovery" element={<MfaPage />} />
      <Route path="mfa/enroll" element={<MfaPage />} />
      <Route path="mfa/recovery-codes" element={<MfaPage />} />
      <Route path="employer-verification-dashboard" element={<Navigate to="/employer-verification" replace />} />
      <Route path="identity-verification-dashboard" element={<Navigate to="/identity-verification" replace />} />
      {/* RBAC Phase 3: OIDC mode needs an in-memory access token here (silent authorize). */}
      <Route element={<OidcSessionGate />}>
      <Route path="identity-verification" element={<IdentityVerificationShell />}>
        <Route index element={<IdentityVerificationDashboard />} />
        <Route path="journeys" element={<IdentityVerificationJourneys />} />
        <Route path="lookup" element={<IdentityVerificationLookup />} />
      </Route>
      <Route element={<Layout />}>
        <Route index element={<RedirectToApplications />} />
        <Route path="dashboard" element={<RedirectToApplications />} />
        <Route path="applications" element={<ApplicationsPage />} />
        <Route path="applications/:applicationId" element={<ApplicationDetailPage />} />
        <Route path="income-verification" element={<IncomeVerificationDashboard />} />
        <Route path="doctrine-config" element={<DoctrineConfigPage />} />
        <Route path="doctrine-config/:id" element={<DoctrineDetailPage />} />
        <Route path="doctrines" element={<RedirectToApplications />} />
        <Route path="doctrines/:id" element={<RedirectLegacyDoctrineDetail />} />
        <Route path="providers" element={<ProvidersPage />} />
        <Route path="providers/:id" element={<ProviderDetailPage />} />
        <Route path="routing" element={<RoutingPage />} />
        <Route path="audit" element={<RedirectToApplications />} />
        {/* Legacy admin-audit tab → the Phase 2 audit page (gated on admin_audit.read there). */}
        <Route path="admin/audit" element={<Navigate to="/admin/audit-log" replace />} />
        {/* Every /admin/* page must have a TAB_PERMISSION key (enforced by adminPermissions.test.ts). */}
        <Route path="admin/users" element={<AdminUsersPage />} />
        <Route path="admin/users/:id" element={<AdminUserDetailPage />} />
        <Route path="admin/groups" element={<AdminGroupsPage />} />
        <Route path="admin/roles" element={<AdminRolesPage />} />
        <Route path="admin/access/expiring" element={<AdminExpiringAccessPage />} />
        <Route path="admin/access/reviews" element={<AdminAccessReviewsPage />} />
        <Route path="admin/access/reviews/:id" element={<AdminAccessReviewDetailPage />} />
        <Route path="admin/access/eligibilities" element={<AdminEligibilitiesPage />} />
        {/* RBAC Phase 4: open to every signed-in staff member (PUBLIC_IN_APP_PATHS). */}
        <Route path="my-access" element={<MyAccessPage />} />
        <Route path="my-access/approvals" element={<MyAccessPage />} />
        <Route path="admin/audit-log" element={<AdminConsoleAuditPage />} />
        <Route path="no-access" element={<NoAccessPage />} />
        <Route path="service-onboarding" element={<ServiceOnboardingPage />} />
        <Route path="service-onboarding/:serviceId" element={<ServiceOnboardingEditorPage />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="mocking" element={<RedirectToApplications />} />
        <Route path="service-configs" element={<RedirectToApplications />} />
        <Route path="service-configs/:serviceKey" element={<RedirectToApplications />} />
        <Route path="employer-verification" element={<EmployerVerificationDashboard />} />
        <Route
          path="employer-verification/analytics"
          element={<EmployerVerificationAnalyticsDashboard />}
        />
        <Route
          path="employer-verification/investigation/:verificationId"
          element={<EmployerInvestigationWorkspacePage />}
        />
        <Route
          path="employer-verification/company/:verificationId"
          element={<CompanyDossierPage />}
        />
        <Route path="*" element={<RedirectToApplications />} />
      </Route>
      </Route>
    </Routes>
  );
}
